import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import useTable from '@/hooks/useTable';
import type { GsEvent } from '@/lib/types';
import { fallbackHero } from '@/lib/placeholders';
import { formatEventDate, isUpcoming } from '@/lib/format';
import DemoBadge from '@/components/feature/DemoBadge';

const SAMPLE_SPONSOR = 'Coastal Mutual Insurance';

export default function SponsoredSpotlight() {
  const { data: events } = useTable<GsEvent>('gs_events');

  const spot = useMemo(() => {
    const upcoming = events
      .filter((e) => e.show_in_calendar !== false && isUpcoming(e.event_date))
      .sort((a, b) => (a.event_date || '').localeCompare(b.event_date || ''));
    return upcoming[0];
  }, [events]);

  if (!spot) return null;

  return (
    <section className="bg-background-100 px-4 md:px-8 py-14 md:py-20">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="font-label uppercase tracking-[0.28em] text-xs text-accent-700 font-semibold">Sponsored placement</span>
        <DemoBadge label="Preview" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="w-full h-64 md:h-80 lg:h-full overflow-hidden">
          <img
            src={spot.hero_image || fallbackHero}
            alt={spot.name}
            title={`${spot.name} — sponsored placement`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="p-6 md:p-8 lg:py-10">
          <p className="font-label uppercase tracking-widest text-[11px] text-foreground-500">
            Presented by <span className="text-foreground-900 font-semibold">{SAMPLE_SPONSOR}</span> (sample)
          </p>
          <h3 className="mt-3 font-heading font-extrabold uppercase text-2xl md:text-4xl text-foreground-950 leading-tight">{spot.name}</h3>
          <p className="mt-3 text-foreground-700 flex items-center gap-1.5 text-sm">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
            {formatEventDate(spot.event_date) || `${spot.month_label} ${spot.day_label}`}
            <span className="mx-1 text-foreground-400">·</span>
            {spot.course}, {spot.city}
          </p>
          <p className="mt-4 text-foreground-600 text-sm">
            This is how a sponsor&apos;s tournament gets the spotlight — a dedicated homepage feature with their name attached, above the standard calendar listing.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={`/events/${spot.slug}`} className="bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider text-sm px-5 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors">
              View tournament
            </Link>
            <Link to="/partners" className="border border-foreground-950 text-foreground-950 hover:bg-foreground-950 hover:text-background-50 font-label font-bold uppercase tracking-wider text-sm px-5 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors">
              Sponsor this event
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}